
import { DateUtility } from '../../utilities';
import { ActivitySource, Asset, Contact, ContactEntity, EntitySubStatus, JobTemplate, JobType, Label, LabelEntity, PagingOptions, ServiceTypeTag, SitePro, SiteTiny } from '../';
import { Entity } from '../entity';
import { RecurringJobSchedule } from './recurring.job.schedule';
import * as Enumerable from 'linq';

export interface RecurringJobRule {
    _id: string; // The rule string definition
    name: string;// the rule english description;
}


export enum RecurringJobRoute {
    All = 'all',
    Active = 'active',
    Inactive = 'inactive',
    Expired = 'expired'
}

export enum RecurringJobStatus {
    Active = 'Active',
    Inactive = 'Inactive',
    Expired = 'Expired'
}

export enum RecurringJobStatusSystemName {
    Active = 'Active',
    ActiveStarted = 'Active-Started',
    Inactive = 'Inactive',
    Expired = 'Expired'
}

export enum RecurringJobType {
    Standard = 'Standard',
    Service = 'Service'
}

export const SiteEmptyId = 'Site-00000000-0000-0000-0000-000000000000';

export interface RecurringJob extends Entity {
    notes: string;
    account: Entity;
    site: SiteTiny;
    zones?: Entity[];

    start?: Date;
    /** if empty then it is Never; else it is on Date */
    end?: Date;

    resource?: ContactEntity;
    schedule?: RecurringJobSchedule[];

    jobTemplate?: JobTemplate;

    assets?: Asset[];

    createdAt?: Date;
    createdBy?: Entity;

    updatedAt?: Date;
    updatedBy?: Entity;

    turnOn: boolean;
    //status?: RecurringJobStatus;

    status?: EntitySubStatus;

    /** @description generated up to date */
    lastGeneratedDate?: Date;

    type?: RecurringJobType;

    lastService?: RecurringJobLastService;

    nextService?: RecurringJobNextService

    confirmation?: RecurringJobConfirmation;


    // readonly
    serviceType?: ServiceTypeTag;

    //for bulk import
    batchId?: string;

}

export interface RecurringJobNextService {
    dueOn?: Date;
    job?: Entity;
}

export interface RecurringJobLastService {
    completedOn?: Date;
    completedBy?: Entity;
    job?: Entity;
}

export interface RecurringJobConfirmation {
    required: boolean; // copied from job template and can visible and editable
    confirmedOn?: Date;
    confirmedBy?: Entity;
}


export interface RecurringJobTable extends Entity {
    account: Entity;
    customer: string;
    start: Date;
    end: Date;
    template: string;
    status: RecurringJobStatus;
    timezone: string;
    lastGeneratedDate: Date;
}

export interface RecurringJobRefinement {
    customerIds?: string[];
    search?: string;
}

export interface RecurringJobRequest {
    refinement?: RecurringJobRefinement;
    paging?: PagingOptions;
}

export interface RecurringJobResponse {
    data: RecurringJobTable[];
    metadata: { total: number; }

}

export interface ReccurringJobSchedule {
    job: RecurringJob;
    instances: RecurringJobInstance[];
}

export interface RecurringJobInstance {
    date: Date;
    createAppointment?: boolean;
    startTime?: Date;
    endTime?: Date;
    resources?: ContactEntity[];
    contractor?: ContactEntity;
    scheduleId: string;
}


export interface RecurringJobShortCreateRequest {
    siteId: string;
    template: Entity;
    baselineDate: Date;
    knowLastServiceDate: boolean;
    source: ActivitySource;
    forceConfirmation: boolean;
    reactiveServiceId?: string;
}

export interface RecurringJobQuickCreateRequest {
    name: string;

    account: Contact;
    site: SitePro;
    contact: Contact;

    owner: Contact;
    jobType?: JobType;
    tags?: Label[];

    schedule: RecurringJobSchedule;
}


export interface ServiceEntity extends Entity {
    requiresConfirmation: boolean;
    nextServiceDate?: Date;
    nextConfirmationDate?: Date;
    nextServiceJob?: Entity;
    serviceType?: LabelEntity;
    status?: EntitySubStatus;
}

export enum EntityRecurringJobCommandIds {
    QuickCreate = 'QuickCreate',
}

export type RecurringJobCommands =
    {
        commandId: EntityRecurringJobCommandIds.QuickCreate,
        data: RecurringJobQuickCreateRequest
    }


export function getRequireConfirmationDateFromSite(site: SitePro) {

    const requiresConfirmationDate = Enumerable
        .from(site?.services)
        .select(service => getRequireConfirmationDateFromServiceEntity(service))
        .where(x => x !== null)
        .orderBy(x => x)
        .firstOrDefault();

    return requiresConfirmationDate ?? DateUtility.maxDateTime.toJSDate();
}

export function getRequireConfirmationDateFromServiceEntity(service: ServiceEntity) {

    const requiresConfirmation = service?.requiresConfirmation;
    const noNextServiceJob = !service?.nextServiceJob;
    const futureDate = DateUtility.maxDateTime.toJSDate();
    const safeNextConfirmationDate = DateUtility.getDate(service?.nextConfirmationDate ?? futureDate);

    return (requiresConfirmation && noNextServiceJob)
        ? safeNextConfirmationDate
        : futureDate
}

export function toServiceEntity(job: RecurringJob) {
    return {
        _id: job?._id,
        name: job?.name,
        serviceType: job?.serviceType,
        requiresConfirmation: !job?.turnOn,
        nextConfirmationDate: job?.nextService?.dueOn
            ? DateUtility.getDateTime(job?.nextService?.dueOn).endOf('day').minus({ days: 60 }).toJSDate()
            : null,
        nextServiceDate: job?.nextService?.dueOn,
        nextServiceJob: job?.nextService?.job ?? null,
    } as ServiceEntity
}